import "./SimpleGallery.css";
import React, { useState, useEffect, useCallback, useRef } from "react";

const Gallery = ({ folder = "foto" }) => {
  // Состояние
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [search, setSearch] = useState("");
  const [clickCount, setClickCount] = useState(0); // Для защиты от быстрых кликов
  const [currentIndex, setCurrentIndex] = useState(-1); // Для навигации в модальном окне

//const API_URL = "http://localhost:5001/api";
 const API_URL = process.env.NODE_ENV === 'production' 
    ? '/api'  // В продакшене через nginx
    : 'http://localhost:5000/api'; // В разработке напрямую
  const lastClickTime = useRef(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // ========== ЗАГРУЗКА ИЗОБРАЖЕНИЙ ==========
  const loadAllImages = useCallback(async () => {
    console.log(`🚀 Загрузка из ${folder} (скачивание запрещено)`);

    setLoading(true);
    setError(null);

    try {
      const startTime = Date.now();

      const response = await fetch(`${API_URL}/gallery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folder }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const loadTime = Date.now() - startTime;
        console.log(
          `✅ Загружено ${data.images.length} изображений за ${loadTime}ms`,
        );
        console.log("🔒 Режим безопасности:", data.security?.message);

        setImages(data.images);

        if (data.images.length === 0) {
          setError("В папке нет изображений");
        }
      } else {
        setError(data.error || "Ошибка загрузки");
      }
    } catch (err) {
      console.error("❌ Ошибка сети:", err);
      setError("Сервер не доступен");
    } finally {
      setLoading(false);
    }
  }, [folder]);

  // ========== ПРИ ЗАГРУЗКЕ СТРАНИЦЫ ==========
  useEffect(() => {
    console.log("📄 Страница загружается...");
    loadAllImages();
  }, [loadAllImages]);

  // ========== ЗАЩИТА ОТ СКАЧИВАНИЯ ==========

  // Запрет контекстного меню (правая кнопка мыши)
  useEffect(() => {
    const preventContextMenu = (e) => {
      e.preventDefault();
      console.log("🚫 Контекстное меню заблокировано");
      return false;
    };

    // Запрет перетаскивания
    const preventDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Запрет копирования
    const preventCopy = (e) => {
      e.preventDefault();
      console.log("🚫 Копирование заблокировано");
      return false;
    };

    // Запрет сочетаний клавиш для сохранения
    const preventSaveShortcut = (e) => {
      // Ctrl+S, Ctrl+Shift+S, Cmd+S
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        console.log("🚫 Сохранение заблокировано");
        return false;
      }
    };

    // Блокировка DevTools
    const blockDevTools = (e) => {
      if (
        e.keyCode === 123 || // F12
        (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
        (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
        (e.ctrlKey && e.keyCode === 85)
      ) {
        // Ctrl+U
        e.preventDefault();
        alert("🚫 Доступ запрещен!");
        return false;
      }
    };

    document.addEventListener("contextmenu", preventContextMenu);
    document.addEventListener("dragstart", preventDragStart);
    document.addEventListener("copy", preventCopy);
    document.addEventListener("keydown", preventSaveShortcut);
    document.addEventListener("keydown", blockDevTools);

    return () => {
      document.removeEventListener("contextmenu", preventContextMenu);
      document.removeEventListener("dragstart", preventDragStart);
      document.removeEventListener("copy", preventCopy);
      document.removeEventListener("keydown", preventSaveShortcut);
      document.removeEventListener("keydown", blockDevTools);
    };
  }, []);

  // ========== ФИЛЬТРАЦИЯ И ФОРМАТИРОВАНИЕ ==========

  const filteredImages = React.useMemo(() => {
    if (!search.trim()) return images;

    const query = search.toLowerCase();
    return images.filter((img) => img.filename.toLowerCase().includes(query));
  }, [images, search]);

  const formatSize = useCallback((bytes) => {
    if (!bytes) return "0 B";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }, []);

  const formatDate = useCallback((date) => {
    return new Date(date).toLocaleDateString("ru-RU");
  }, []);

  // ========== ОБРАБОТЧИКИ ==========

  // Защищенный клик по изображению
  const handleImageClick = useCallback(
    (image, index) => {
      const now = Date.now();

      // Защита от быстрых кликов (не более 1 клика в 500ms)
      if (now - lastClickTime.current < 500) {
        console.log("⏳ Слишком быстрый клик, игнорируем");
        return;
      }

      lastClickTime.current = now;

      // Если уже выбрано это изображение - закрываем
      if (selectedImage?.id === image.id) {
        setSelectedImage(null);
        setCurrentIndex(-1);
      } else {
        // Иначе открываем новое
        setSelectedImage(image);
        setCurrentIndex(index);
        setClickCount((prev) => prev + 1);
      }
    },
    [selectedImage],
  );

  // Закрытие модального окна
  const handleCloseModal = useCallback(() => {
    setSelectedImage(null);
    setCurrentIndex(-1);
  }, []);

  // Клик по открытому изображению (закрытие)
  const handleModalImageClick = useCallback((e) => {
    e.stopPropagation();
    setSelectedImage(null);
    setCurrentIndex(-1);
  }, []);

  // Навигация по изображениям
  const handleNextImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();

      if (filteredImages.length === 0) return;

      const nextIndex = (currentIndex + 1) % filteredImages.length;
      setSelectedImage(filteredImages[nextIndex]);
      setCurrentIndex(nextIndex);
    },
    [filteredImages, currentIndex],
  );

  const handlePrevImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();

      if (filteredImages.length === 0) return;

      const prevIndex =
        currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
      setSelectedImage(filteredImages[prevIndex]);
      setCurrentIndex(prevIndex);
    },
    [filteredImages, currentIndex],
  );

  // Обработчик клавиатуры для навигации
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          handlePrevImage();
          break;
        case "ArrowRight":
        case " ":
          handleNextImage();
          break;
        case "Escape":
          handleCloseModal();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, handlePrevImage, handleNextImage, handleCloseModal]);

  // Обработчик свайпов для мобильных устройств
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Попытка скачивания (блокируется)
  const handleDownloadAttempt = useCallback(() => {
    console.log("🚫 Попытка скачивания заблокирована");

    // Показываем сообщение
    alert("❌ Скачивание изображений запрещено администратором");

    // Можно добавить логирование попыток
    const attemptData = {
      timestamp: new Date().toISOString(),
      image: selectedImage?.filename,
      action: "download_attempt",
      blocked: true,
    };
    console.log("📝 Логирование попытки скачивания:", attemptData);
  }, [selectedImage]);

  // Обновление
  const handleRefresh = useCallback(() => {
    loadAllImages();
  }, [loadAllImages]);

  // Добавляем CSS анимации
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideInLeft {
        from { transform: translateX(-30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideInRight {
        from { transform: translateX(30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // ========== РЕНДЕРИНГ ==========

  return (
    <div className="container2">
      {/* Сообщение об ошибке */}
      {error && !loading && (
        <div className="error">
          <div>
            <strong>⚠️ Внимание:</strong> {error}
          </div>
          <button
            onClick={handleRefresh}
            style={{
              padding: "10px 20px",
              background: "rgba(52, 152, 219, 0.2)",
              color: "#3498db",
              border: "1px solid rgba(52, 152, 219, 0.3)",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Попробовать снова
          </button>
        </div>
      )}

      {/* Загрузка */}
      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p
            style={{
              marginTop: "20px",
              fontSize: "18px",
              color: "#fff",
            }}
          >
            Загружаем изображения...
          </p>
        </div>
      )}

      {/* Нет изображений */}
      {!loading &&
        filteredImages.length === 0 &&
        images.length === 0 &&
        !error && (
          <div className="empty">
            <div
              style={{ fontSize: "80px", marginBottom: "20px", opacity: 0.3 }}
            >
              📁
            </div>
            <h3 style={{ color: "#fff", marginBottom: "15px" }}>Папка пуста</h3>
            <p
              style={{
                color: "#a0aec0",
                maxWidth: "500px",
                margin: "0 auto 30px",
                lineHeight: "1.6",
              }}
            >
              Добавьте изображения в папку <strong>{folder}</strong> на сервере
            </p>
            <button
              onClick={handleRefresh}
              style={{
                padding: "12px 30px",
                background: "linear-gradient(135deg, #3498db 0%, #2980b9 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Проверить снова
            </button>
          </div>
        )}

      {/* Галерея */}
      {!loading && filteredImages.length > 0 && (
        <div className="grid">
          {filteredImages.map((image, index) => {
            const isSelected = selectedImage?.id === image.id;

            return (
              <div
                className="card"
                key={image.id}
                onClick={() => handleImageClick(image, index)}
                style={{
                  transform: isSelected ? "scale(0.98)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 20px rgba(0, 0, 0, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              >
                <div className="imageContainer">
                  <img
                    src={image.data}
                    alt={image.filename}
                    className="image"
                    loading="lazy"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно */}
      {selectedImage && (
        <div
          className="modalOverlay"
          onClick={handleCloseModal}
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            {/* Кнопка "Назад" */}
            <button
              className="navButton prevButton"
              onClick={handlePrevImage}
              title="Предыдущее фото (←)"
            >
              ‹
            </button>

            {/* Изображение */}
            <div className="modalBody" onClick={handleModalImageClick}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={selectedImage.data}
                  alt={selectedImage.filename}
                  className="modalImage"
                  title="Только просмотр. Скачивание запрещено. Используйте стрелки ← → для навигации"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    alert(
                      "❌ Контекстное меню заблокировано. Скачивание запрещено.",
                    );
                  }}
                />
              </div>
            </div>

            {/* Кнопка "Вперед" */}
            <button
              className="navButton nextButton"
              onClick={handleNextImage}
              title="Следующее фото (→ или пробел)"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
